import React, { useState, useRef, useEffect } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Button from './button';
import IconWhatsApp from '$partials/icons/icon-whatsapp';
import IconFacebook from '$partials/icons/icon-facebook';
import IconLinkedIn from '$partials/icons/icon-linkedin';
import IconX from '$partials/icons/icon-x';
import IconClose from '$partials/icons/icon-close';
import IconTick from '$partials/icons/icon-tick';
import IconLink from '$partials/icons/icon-link';
import IconShare from '$partials/icons/icon-share';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/ui.d.ts').ShareButtonModal}
 */

const ShareButtonModal = ({ title, author, propertyRef, url, className = '', height, width }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const dialogRef = useRef(null);
    const scrollElementRef = useRef(null);

    // Use provided URL or fall back to current page URL (client-side only to avoid hydration mismatch)
    const [shareUrl, setShareUrl] = useState(url || '');

    useEffect(() => {
        if (!url && typeof window !== 'undefined') {
            setShareUrl(window.location.href);
        }
    }, [url]);

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(title);

    const handleClose = () => {
        setIsOpen(false);
        setCopied(false);
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    useEffect(() => {
        const dialogNode = dialogRef.current;
        const scrollElementNode = scrollElementRef.current;

        if (isOpen) {
            if (dialogNode && !dialogNode.open && scrollElementNode) {
                disableBodyScroll(scrollElementNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open && scrollElementNode) {
                enableBodyScroll(scrollElementNode);
                dialogNode.close?.();
            }
        }

        const handleEscapeKey = (/** @type {KeyboardEvent} */ event) => {
            if (event.key === 'Escape' && isOpen) {
                event.preventDefault();
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            enableBodyScroll(scrollElementNode);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen]);

    const shareLinks = [
        {
            name: 'WhatsApp',
            icon: IconWhatsApp,
            url: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
            ariaLabel: 'Share on WhatsApp'
        },
        {
            name: 'Facebook',
            icon: IconFacebook,
            url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            ariaLabel: 'Share on Facebook'
        },
        {
            name: 'LinkedIn',
            icon: IconLinkedIn,
            url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            ariaLabel: 'Share on LinkedIn'
        },
        {
            name: 'X',
            icon: IconX,
            url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
            ariaLabel: 'Share on X'
        }
    ];

    return (
        <>
            <Button
                as='button'
                className={`${height} ${width} shrink-0 bg-grey-grey-0 rounded flex justify-center items-center hover:bg-button-primary-transparent-background-hover hover:text-button-primary-transparent-text-hover`}
                onClick={() => setIsOpen(true)}>
                <IconShare className='h-[14px]' />
            </Button>

            <dialog
                className='dialog-reset'
                ref={dialogRef}>
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center p-16 bg-black/50 theme-light'
                    onClick={handleClose}
                    ref={scrollElementRef}>
                    <div
                        className='bg-grey-grey-0 shadow-xl max-w-[580px] w-full mx-auto'
                        onClick={(e) => e.stopPropagation()}>

                        {/* Header */}
                        <div className='flex items-center justify-between px-20 pt-20 pb-18'>
                            <p className='type-style-eyebrow text-main-text-secondary'>
                                SHARE
                            </p>
                            <Button
                                as='button'
                                className='flex items-center justify-center text-main-text-primary'
                                onClick={handleClose}
                                type='button'>
                                <IconClose />
                            </Button>
                        </div>

                        {/* Content */}
                        <div className='px-20 pb-20'>
                            {/* Title */}
                            <h2 className='type-style-3 text-main-text-primary mb-12'>
                                {title}
                            </h2>

                            {/* Property Reference (for properties) */}
                            {propertyRef && (
                                <p className='type-style-eyebrow text-main-text-secondary mb-20'>
                                    PROPERTY REF: {propertyRef}
                                </p>
                            )}

                            {/* Author (for articles) */}
                            {author && (
                                <p className='type-style-5 text-main-text-secondary mb-20'>
                                    {author}
                                </p>
                            )}

                            {/* URL Field with Copy Button */}
                            <div className='flex items-center gap-8 mb-24 p-12 bg-input-background border border-input-border rounded'>
                                <p className='flex-1 type-style-eyebrow text-main-text-secondary truncate'>
                                    {shareUrl}
                                </p>
                                <Button
                                    as='button'
                                    className='flex items-center justify-center w-32 h-32 hover:opacity-70 transition-opacity flex-shrink-0'
                                    onClick={handleCopyLink}>
                                    {copied ? (
                                        <IconTick className='w-16 h-16 text-main-text-primary' />
                                    ) : (
                                        <IconLink className='w-16 h-16 text-main-text-primary' />
                                    )}
                                </Button>
                            </div>

                            {/* Social Icons */}
                            <div className='flex items-center gap-12'>
                                {shareLinks.map((link) => {
                                    const Icon = link.icon;
                                    return (
                                        <Button
                                            as='a'
                                            className='bg-button-secondary-filled-background text-button-secondary-filled-text hover:bg-button-secondary-filled-background-hover hover:text-button-secondary-filled-text-hover flex items-center justify-center w-32 h-32 sm:w-[44px] sm:h-[44px] rounded'
                                            href={link.url}
                                            key={link.name}>
                                            <Icon className='text-main-text-primary' />
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </dialog>
        </>
    );
};

/**
 * @type {import('$types/components/partials/ui.d.ts').ShareButtonModal}
 */

const ShareButtons = ({ title, url }) => {
    const [copied, setCopied] = useState(false);

    // Use provided URL or fall back to current page URL (client-side only to avoid hydration mismatch)
    const [shareUrl, setShareUrl] = useState(url || '');

    useEffect(() => {
        if (!url && typeof window !== 'undefined') {
            setShareUrl(window.location.href);
        }
    }, [url]);

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(title);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };


    const shareLinks = [
        {
            name: 'WhatsApp',
            icon: IconWhatsApp,
            url: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
            ariaLabel: 'Share on WhatsApp'
        },
        {
            name: 'Facebook',
            icon: IconFacebook,
            url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            ariaLabel: 'Share on Facebook'
        },
        {
            name: 'LinkedIn',
            icon: IconLinkedIn,
            url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            ariaLabel: 'Share on LinkedIn'
        },
        {
            name: 'X',
            icon: IconX,
            url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
            ariaLabel: 'Share on X'
        }
    ];

    return (
        <div className='layout-container layout-site-outer-padding py-24 sm:py-32 md:py-40'>
            <div className='col-wrapper flex'>
                <div className='hidden sm:block col-padding sm:w-1/12 md:w-2/12 lg:w-3/12' />

                {/* Social Icons */}
                <div className='col-padding'>
                    <p className='type-style-eyebrow'>
                        SHARE
                    </p>
                    <div className='flex items-center gap-12 mt-16'>
                        <Button
                            as='button'
                            className='bg-button-secondary-filled-background text-button-secondary-filled-text hover:bg-button-secondary-filled-background-hover hover:text-button-secondary-filled-text-hover flex items-center justify-center w-32 h-32 rounded'
                            onClick={handleCopyLink}>
                            {copied ? (
                                <IconTick className='w-16 h-16 text-main-text-primary' />
                            ) : (
                                <IconLink className='w-16 h-16 text-main-text-primary' />
                            )}
                        </Button>
                        {shareLinks.map((link) => {
                            const Icon = link.icon;
                            return (
                                <Button
                                    as='a'
                                    className='bg-button-secondary-filled-background text-button-secondary-filled-text hover:bg-button-secondary-filled-background-hover hover:text-button-secondary-filled-text-hover flex items-center justify-center w-32 h-32 rounded'
                                    href={link.url}
                                    key={link.name}>
                                    <Icon className='text-main-text-primary' />
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export { ShareButtonModal };
export { ShareButtons };

